import { z } from 'zod';
import { api } from './client';
import { formatPrice, formatExitReason } from '../lib/formatters';
import type {
  DashboardMetrics,
  EquityCurvePoint,
  OrderTimelineItem,
  Position,
  Trade,
  TradeDetail,
  TradeChartData,
  ChartMarker,
  ChartMarkerKind,
  Signal,
  SignalDetailResponse,
  RiskStatus,
  RiskEvent,
  Alert,
  OperatorAction,
  PaginatedResponse,
  TradeFilters,
  SignalFilters,
  AlertFilters,
  AuditFilters,
  SystemSettings,
  SystemStatus,
  PositionControls,
  TelegramBotConfig,
  ExchangeCredential,
  ExchangeType,
  TradeDeal,
  TboGlobalConfig,
  ScoringSettingsValue,
  PnlCalendarResponse,
} from '../types';

const pnlCalendarDaySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  pnlUsd: z.number(),
  tradeCount: z.number().int().nonnegative(),
  winCount: z.number().int().nonnegative(),
  lossCount: z.number().int().nonnegative(),
  breakevenCount: z.number().int().nonnegative(),
  entryNotionalClosedUsd: z.number().nonnegative(),
  signalsLongEntryReceived: z.number().int().nonnegative(),
  signalsShortEntryReceived: z.number().int().nonnegative(),
  signalsExitReceived: z.number().int().nonnegative(),
  signalsOtherReceived: z.number().int().nonnegative(),
  entryDecisionsApproved: z.number().int().nonnegative(),
  entryDecisionsRejected: z.number().int().nonnegative(),
  exitDecisionsApproved: z.number().int().nonnegative(),
  exitDecisionsRejected: z.number().int().nonnegative(),
});

const pnlCalendarResponseSchema = z.object({
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  days: z.array(pnlCalendarDaySchema),
  monthTotalUsd: z.number(),
});

function safeDisplayNumber(value: number | undefined | null): number | undefined {
  if (value == null) return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export const dashboardApi = {
  getMetrics: () => api.get<DashboardMetrics>('/dashboard/metrics'),
  getPnlCalendar: async (params: { year: number; month: number }): Promise<PnlCalendarResponse> => {
    const raw = await api.get<unknown>('/dashboard/pnl-calendar', params);
    return pnlCalendarResponseSchema.parse(raw);
  },
  getEquityCurve: (
    period: '1D' | '7D' | '30D' | '90D' | 'ALL',
    opts?: { series?: 'account' | 'realized' }
  ) =>
    api.get<EquityCurvePoint[]>('/dashboard/equity-curve', {
      period,
      ...(opts?.series != null ? { series: opts.series } : {}),
    }),
  getSystemStatus: () => api.get<SystemStatus>('/dashboard/status'),
};

export interface SkippedExitSignalData {
  signalId: string;
  receivedAt: string;
  price: number;
  reason: string;
  decisionTraceJson?: {
    exitSignalContext?: {
      rMultipleAtExit?: number;
      timeSinceEntrySeconds?: number;
      unrealizedPnlAtExit?: number;
      exitPrice?: number;
    };
  };
}

export interface PositionDetailResponse {
  position: Position & {
    qty?: number;
    initialStop?: number;
    currentStop?: number;
    takeProfit?: number | null;
    status?: string;
    closedAt?: string | null;
    exitPrice?: number | null;
    realizedPnl?: number | null;
    unrealizedPnl?: number;
    peakLow?: { peak: { price: number; at: string }; low: { price: number; at: string } } | null;
    originalQty?: number | null;
    exitSignalCount?: number;
    partialCloseCount?: number;
    partialRealizedPnl?: number;
  };
  orders: unknown[];
  fills: unknown[];
  decision: unknown;
  signal: unknown;
  skippedExitSignals?: SkippedExitSignalData[];
  acceptedExitSignals?: SkippedExitSignalData[];
  equityImpact: { entryEquity: number; exitEquity: number; rMultiple: number };
  mt5Command?: {
    id: string;
    action: string;
    status: string;
    terminalId: string;
    symbol: string;
    volume: number | null;
    stopLoss: number | null;
    takeProfit: number | null;
    referencePrice: number | null;
    createdAt: string;
    expiresAt: string;
  } | null;
  mt5Execution?: {
    success: boolean;
    ticket: number;
    fillPrice: number;
    filledVolume: number;
    brokerRetcode: number;
    brokerRetcodeText: string;
    errorMessage: string | null;
    executedAt: string;
  } | null;
  deals?: TradeDeal[];
  mt5Timeline?: Array<{
    timestamp: string;
    action: string;
    status: string;
    fillPrice?: number;
    executedAt?: string;
    filledVolume?: number;
    comment?: string | null;
    dealPnl?: number;
  }>;
  chartData?: TradeChartData;
}

export const positionsApi = {
  getOpen: () => api.get<Position[]>('/positions/open'),
  getById: (id: string) => api.get<PositionDetailResponse>(`/positions/${id}`),
  close: (id: string, justification: string) =>
    api.post<void>(`/positions/${id}/close`, { justification }),
};

/** Raw trades API response (PositionData from server) */
interface TradesApiResponse {
  data: Array<{
    id: string;
    symbol: string;
    side: string;
    entryPrice: number;
    exitPrice: number | null;
    qty: number;
    initialStop: number;
    realizedPnl: number | null;
    openedAt: string;
    closedAt: string | null;
    exitReason: string | null;
    originalQty?: number | null;
    exitSignalCount?: number;
    partialCloseCount?: number;
    partialRealizedPnl?: number;
    maxAdverseExcursion: number;
    maxFavorableExcursion: number;
  }>;
  pagination: { page: number; limit: number; total: number; pages: number };
}

function toTrade(item: TradesApiResponse['data'][0]): Trade {
  const qty = Number(item.qty);
  const entryPrice = Number(item.entryPrice);
  const exitPrice = item.exitPrice != null ? Number(item.exitPrice) : 0;
  const realizedPnl = item.realizedPnl != null ? Number(item.realizedPnl) : 0;
  const riskPerUnit = Math.abs(entryPrice - Number(item.initialStop));
  const totalRisk = riskPerUnit * qty;
  const rMultiple = totalRisk > 0 ? realizedPnl / totalRisk : 0;
  const positionValue = entryPrice * qty;
  const pnlPercent = positionValue > 0 ? (realizedPnl / positionValue) * 100 : 0;

  let duration = '';
  if (item.openedAt && item.closedAt) {
    const opened = new Date(item.openedAt).getTime();
    const closed = new Date(item.closedAt).getTime();
    const mins = Math.round((closed - opened) / 60_000);
    if (mins < 60) duration = `${mins}m`;
    else if (mins < 1440) duration = `${Math.round(mins / 60)}h`;
    else duration = `${Math.round(mins / 1440)}d`;
  }

  return {
    id: item.id,
    symbol: item.symbol,
    side: item.side as Trade['side'],
    entryPrice,
    exitPrice: exitPrice || entryPrice,
    initialStop: Number(item.initialStop),
    quantity: qty,
    rMultiple,
    pnl: realizedPnl,
    pnlPercent,
    duration,
    openedAt: item.openedAt,
    closedAt: item.closedAt ?? item.openedAt,
    exitReason: item.exitReason ?? '',
    mae: safeDisplayNumber(item.maxAdverseExcursion) ?? 0,
    mfe: safeDisplayNumber(item.maxFavorableExcursion) ?? 0,
    originalQty: item.originalQty,
    exitSignalCount: item.exitSignalCount,
    partialCloseCount: item.partialCloseCount,
    partialRealizedPnl: item.partialRealizedPnl,
  };
}

/** Raw trade detail API response */
interface TradeDetailApiResponse {
  trade: {
    id: string;
    symbol: string;
    side: string;
    entryPrice: number;
    exitPrice: number | null;
    qty: number;
    initialStop: number;
    currentStop: number;
    takeProfit: number | null;
    realizedPnl: number | null;
    openedAt: string;
    closedAt: string | null;
    exitReason: string | null;
    maxAdverseExcursion: number;
    maxFavorableExcursion: number;
    peakLow?: { peak: { price: number; at: string }; low: { price: number; at: string } } | null;
    originalQty?: number | null;
    exitSignalCount?: number;
    partialCloseCount?: number;
    partialRealizedPnl?: number;
  };
  orders: Array<{
    id: string;
    symbol: string;
    side: string;
    type: string;
    qty: number;
    price: number | null;
    status: string;
    createdAt: string;
  }>;
  decisionTrace: object;
  skippedExitSignals?: SkippedExitSignalData[];
  acceptedExitSignals?: SkippedExitSignalData[];
  chartData?: {
    candles: Array<{ timestamp: string; open: number; high: number; low: number; close: number; volume: number }>;
    markers: Array<{ time: string; type: string; price: number; label?: string }>;
    chartWarning?: string;
    intervalUsed?: string;
  };
  mt5Timeline?: Array<{
    timestamp: string;
    action: string;
    status: string;
    fillPrice?: number;
    executedAt?: string;
    filledVolume?: number;
    comment?: string | null;
    dealPnl?: number;
  }>;
  deals?: Array<{
    type: 'in' | 'out';
    side: 'buy' | 'sell';
    qty: number;
    price: number;
    executedAt: string;
    dealPnl?: number;
    trigger?: string | null;
  }>;
  metrics: {
    mae: number;
    mfe: number;
    rMultiple: number;
    slippage?: number;
    holdingPeriodCandles?: number;
  };
}

const SKIP_KEYS = new Set(['timestamp', 'signalId', 'userId']);

const CHART_MARKER_KINDS = new Set<string>([
  'entry',
  'exit',
  'stop',
  'target',
  'exit_signal_skipped',
  'exit_signal_accepted',
  'fill',
  'peak',
  'low',
]);

function parseChartMarker(raw: {
  time: string;
  type: string;
  price: number;
  label?: string;
}): ChartMarker | null {
  if (raw == null || typeof raw.time !== 'string' || typeof raw.type !== 'string') return null;
  if (Number.isNaN(Date.parse(raw.time))) return null;
  const price = Number(raw.price);
  if (!Number.isFinite(price)) return null;
  if (!CHART_MARKER_KINDS.has(raw.type)) return null;
  return {
    time: raw.time,
    type: raw.type as ChartMarkerKind,
    price,
    ...(raw.label != null && raw.label !== '' ? { label: raw.label } : {}),
  };
}

function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/_/g, ' ')
    .trim();
}

function flattenDecisionTrace(
  obj: unknown,
  prefix = ''
): Array<{ label: string; passed: boolean; detail?: string }> {
  const result: Array<{ label: string; passed: boolean; detail?: string }> = [];
  if (obj == null || typeof obj !== 'object') return result;

  const record = obj as Record<string, unknown>;

  if ('pass' in record && typeof record.pass === 'boolean') {
    const label = prefix ? humanizeKey(prefix) : 'Check';
    const detail =
      record.reason != null
        ? String(record.reason)
        : record.detail != null
          ? String(record.detail)
          : undefined;
    result.push({ label, passed: record.pass === true, detail });
    return result;
  }

  if ('qty' in record || 'riskAmount' in record || 'stopDistance' in record) {
    const parts: string[] = [];
    if (record.qty != null) parts.push(`qty ${Number(record.qty)}`);
    if (record.riskAmount != null) parts.push(`risk $${Number(record.riskAmount).toFixed(2)}`);
    if (record.stopDistance != null) parts.push(`stop ${Number(record.stopDistance)}`);
    if (parts.length > 0) {
      result.push({
        label: prefix ? humanizeKey(prefix) : 'Sizing',
        passed: true,
        detail: parts.join(', '),
      });
    }
    return result;
  }

  for (const [key, value] of Object.entries(record)) {
    if (SKIP_KEYS.has(key)) continue;
    if (value == null) continue;
    if (typeof value === 'object' && !Array.isArray(value)) {
      result.push(...flattenDecisionTrace(value, key));
    }
  }
  return result;
}

function toTradeDetail(res: TradeDetailApiResponse): TradeDetail {
  const t = res.trade;
  const m = res.metrics;
  const qty = Number(t.qty);
  const entryPrice = Number(t.entryPrice);
  const exitPrice = t.exitPrice != null ? Number(t.exitPrice) : entryPrice;
  const realizedPnl = t.realizedPnl != null ? Number(t.realizedPnl) : 0;
  const riskPerUnit = Math.abs(entryPrice - Number(t.initialStop));
  const totalRisk = riskPerUnit * qty;
  const rMultiple = m?.rMultiple ?? (totalRisk > 0 ? realizedPnl / totalRisk : 0);
  const positionValue = entryPrice * qty;
  const pnlPercent = positionValue > 0 ? (realizedPnl / positionValue) * 100 : 0;

  let duration = '';
  if (t.openedAt && t.closedAt) {
    const opened = new Date(t.openedAt).getTime();
    const closed = new Date(t.closedAt).getTime();
    const mins = Math.round((closed - opened) / 60_000);
    if (mins < 60) duration = `${mins}m`;
    else if (mins < 1440) duration = `${Math.round(mins / 60)}h`;
    else duration = `${Math.round(mins / 1440)}d`;
  }

  const decisionTraceRaw = res.decisionTrace;
  let decisionTrace: Array<{ label: string; passed: boolean; detail?: string }> = [];
  if (Array.isArray(decisionTraceRaw)) {
    decisionTrace = decisionTraceRaw
      .filter((x): x is { label: string; passed: boolean; detail?: string } =>
        x != null && typeof x === 'object' && typeof (x as { label?: unknown }).label === 'string'
      )
      .map((x) => ({ label: x.label, passed: x.passed === true, detail: x.detail }));
  } else if (decisionTraceRaw != null && typeof decisionTraceRaw === 'object') {
    decisionTrace = flattenDecisionTrace(decisionTraceRaw);
  }

  const ORDER_STATUS_VALUES: OrderTimelineItem['status'][] = [
    'PENDING',
    'FILLED',
    'CANCELLED',
    'REJECTED',
  ];

  let orderTimeline: OrderTimelineItem[];
  if ((res.orders ?? []).length > 0) {
    orderTimeline = res.orders!.map((o) => {
      const status: OrderTimelineItem['status'] = ORDER_STATUS_VALUES.includes(
        o.status as OrderTimelineItem['status']
      )
        ? (o.status as OrderTimelineItem['status'])
        : 'PENDING';
      return {
        timestamp: o.createdAt,
        type: o.type,
        action: `${o.side} ${o.qty}`,
        price: o.price ?? undefined,
        status,
      };
    });
  } else if ((res.mt5Timeline ?? []).length > 0) {
    const actionLabels: Record<string, string> = {
      BUY: 'Entry BUY',
      SELL: 'Entry SELL',
      CLOSE_SYMBOL: 'Exit CLOSE_SYMBOL',
    };
    orderTimeline = res.mt5Timeline!.map((m) => {
      const isPartial =
        m.comment === 'TBO_PARTIAL_TP' ||
        (typeof m.comment === 'string' && m.comment.startsWith('TBO_PARTIAL_TP|'));
      const baseAction = actionLabels[m.action] ?? m.action;
      const volSuffix =
        isPartial && m.filledVolume != null && Number.isFinite(m.filledVolume)
          ? ` ${m.filledVolume} lots`
          : '';
      return {
        timestamp: m.executedAt ?? m.timestamp,
        type: m.action,
        action: isPartial ? `Partial close ${m.action}${volSuffix}` : baseAction,
        price: m.fillPrice,
        status: (m.status === 'executed' ? 'FILLED' : m.status === 'failed' ? 'REJECTED' : 'PENDING') as OrderTimelineItem['status'],
        dealPnl: m.dealPnl,
      };
    });
  } else {
    orderTimeline = [
      {
        timestamp: t.openedAt,
        type: 'ENTRY',
        action: `Position opened @ ${formatPrice(entryPrice)}`,
        price: entryPrice,
        status: 'FILLED' as OrderTimelineItem['status'],
      },
      ...(t.closedAt
        ? [
            {
              timestamp: t.closedAt,
              type: 'EXIT',
              action: `Position closed @ ${formatPrice(exitPrice)} (${formatExitReason(t.exitReason)})`,
              price: exitPrice,
              status: 'FILLED' as OrderTimelineItem['status'],
            },
          ]
        : []),
    ];
  }

  return {
    id: t.id,
    symbol: t.symbol,
    side: t.side as TradeDetail['side'],
    entryPrice,
    exitPrice: exitPrice || entryPrice,
    initialStop: Number(t.initialStop),
    quantity: qty,
    rMultiple,
    pnl: realizedPnl,
    pnlPercent,
    duration,
    openedAt: t.openedAt,
    closedAt: t.closedAt ?? t.openedAt,
    exitReason: t.exitReason ?? '',
    mae: safeDisplayNumber(m?.mae ?? t.maxAdverseExcursion) ?? 0,
    mfe: safeDisplayNumber(m?.mfe ?? t.maxFavorableExcursion) ?? 0,
    stopPrice: safeDisplayNumber(t.initialStop ?? t.currentStop) ?? 0,
    takeProfitPrice: safeDisplayNumber(t.takeProfit) ?? 0,
    peakLow: t.peakLow ?? undefined,
    originalQty: t.originalQty,
    exitSignalCount: t.exitSignalCount,
    partialCloseCount: t.partialCloseCount,
    partialRealizedPnl: t.partialRealizedPnl,
    decisionTrace,
    orderTimeline,
    skippedExitSignals: res.skippedExitSignals ?? [],
    acceptedExitSignals: res.acceptedExitSignals ?? [],
    deals: res.deals ?? [],
    chartData: (() => {
      const cd = res.chartData;
      if (cd == null) {
        return { candles: [], markers: [] as ChartMarker[] };
      }
      const markers = (cd.markers ?? [])
        .map((m) => parseChartMarker(m))
        .filter((m): m is ChartMarker => m != null);
      const out: TradeChartData = {
        candles: Array.isArray(cd.candles) ? cd.candles : [],
        markers,
      };
      if (cd.chartWarning != null && cd.chartWarning !== '') {
        out.chartWarning = cd.chartWarning;
      }
      if (cd.intervalUsed != null && cd.intervalUsed !== '') {
        out.intervalUsed = cd.intervalUsed;
      }
      return out;
    })(),
  };
}

export const tradesApi = {
  getAll: async (
    filters: TradeFilters & { page?: number; pageSize?: number }
  ): Promise<PaginatedResponse<Trade>> => {
    const params: Record<string, string | number | boolean | undefined> = {
      ...(filters.symbol != null && filters.symbol !== '' && { symbol: filters.symbol }),
      ...(filters.side != null && { side: filters.side }),
      ...(filters.outcome != null && { outcome: filters.outcome }),
      ...(filters.minR != null && { minR: filters.minR }),
      ...(filters.maxR != null && { maxR: filters.maxR }),
      ...(filters.startDate != null && filters.startDate !== '' && { from: filters.startDate }),
      ...(filters.endDate != null && filters.endDate !== '' && { to: filters.endDate }),
      page: filters.page ?? 1,
      limit: filters.pageSize ?? 20,
    };
    const res = await api.get<TradesApiResponse>('/trades', params);
    return {
      data: res.data.map(toTrade),
      total: res.pagination.total,
      page: res.pagination.page,
      pageSize: res.pagination.limit,
      totalPages: res.pagination.pages,
    };
  },
  getById: async (id: string): Promise<TradeDetail> => {
    const res = await api.get<TradeDetailApiResponse>(`/trades/${id}`);
    return toTradeDetail(res);
  },
  exportCsv: (filters: TradeFilters) => {
    const params: Record<string, string | number | boolean | undefined> = {
      ...(filters.symbol != null && filters.symbol !== '' && { symbol: filters.symbol }),
      ...(filters.side != null && { side: filters.side }),
      ...(filters.outcome != null && { outcome: filters.outcome }),
      ...(filters.minR != null && { minR: filters.minR }),
      ...(filters.maxR != null && { maxR: filters.maxR }),
      ...(filters.startDate != null && filters.startDate !== '' && { from: filters.startDate }),
      ...(filters.endDate != null && filters.endDate !== '' && { to: filters.endDate }),
    };
    return api.getBlob('/trades/export', params);
  },
};

interface SignalsApiResponse {
  data: Array<{
    id: string;
    receivedAt: string;
    symbol: string;
    timeframe: string;
    event: string;
    valid: boolean;
    rejectReason: string | null;
    decisionApproved: boolean | null;
    decisionReason: string | null;
    riskApproved: boolean | null;
    riskReason: string | null;
    rejectExplanation: string | null;
    scoreFinal: number | null;
    scoreTier: string | null;
    positionId: string | null;
    mt5Symbol: string | null;
    price: number;
    mt5Command: { id: string; action: string; status: string; terminalId: string; createdAt: string } | null;
    mt5Execution: {
      success: boolean;
      errorMessage: string | null;
      brokerRetcode: number;
      brokerRetcodeText: string;
      executedAt: string;
      ticket: number;
    } | null;
  }>;
  pagination: { page: number; limit: number; total: number; pages: number };
}

function toSignalListItem(item: SignalsApiResponse['data'][0]): Signal & { id: string } {
  return {
    id: item.id,
    timestamp: item.receivedAt,
    symbol: item.symbol,
    mt5Symbol: item.mt5Symbol ?? undefined,
    price: safeDisplayNumber(item.price) ?? 0,
    timeframe: item.timeframe,
    event: item.event,
    valid: item.valid,
    approved: item.decisionApproved ?? false,
    riskApproved: item.riskApproved ?? undefined,
    riskReason: item.riskReason ?? undefined,
    rejectReason: item.rejectExplanation ?? item.rejectReason ?? undefined,
    scoreFinal: item.scoreFinal ?? null,
    scoreTier: item.scoreTier ?? null,
    positionId: item.positionId ?? undefined,
    mt5Command: item.mt5Command ?? undefined,
    mt5Execution: item.mt5Execution ?? undefined,
  };
}

export interface ScoringBucketAnalytics {
  bucket: string;
  count: number;
  winRate: number;
  avgPnLR: number;
  avgMfeR: number;
  avgMaeR: number;
  expectancyR: number;
  wins: number;
  losses: number;
  breakevens: number;
}

export interface ScoringAnalyticsSummary {
  totalScored: number;
  totalClosed: number;
  totalWins: number;
  totalLosses: number;
  totalBreakevens: number;
  overallWinRate: number;
  overallExpectancyR: number;
  totalPnLR: number;
}

export interface ScoringAnalyticsFilter {
  symbol?: string;
  from?: string;
  to?: string;
  mode?: 'shadow' | 'size_only' | 'full_effect';
}

/**
 * Server response wrappers for the analytics endpoints. The server emits arrays
 * inside named keys (`tiers`, `factors`, `symbols`, `buckets`) so the client
 * MUST unwrap before iterating — passing the wrapper object straight to
 * `.every()` / `.map()` throws `TypeError: t.every is not a function`.
 *
 * Each `getX()` method does the unwrap + null-safety so callers always receive
 * a plain `ScoringBucketAnalytics[]`. If a future server change drops the
 * wrapper, the `?? response ?? []` fallback chain returns the array directly.
 */
type TierResponse = { tiers: ScoringBucketAnalytics[] } | ScoringBucketAnalytics[] | null | undefined;
type FactorResponse = { factors: ScoringBucketAnalytics[] } | ScoringBucketAnalytics[] | null | undefined;
type BucketResponse =
  | { totalCount: number; buckets: ScoringBucketAnalytics[] }
  | ScoringBucketAnalytics[]
  | null
  | undefined;

function unwrapBucketArray<T extends Record<string, unknown>>(
  resp: T | T[keyof T] | null | undefined,
  key: string
): ScoringBucketAnalytics[] {
  if (resp == null) return [];
  if (Array.isArray(resp)) return resp as ScoringBucketAnalytics[];
  if (typeof resp === 'object' && key in (resp as object)) {
    const val = (resp as Record<string, unknown>)[key];
    return Array.isArray(val) ? (val as ScoringBucketAnalytics[]) : [];
  }
  return [];
}

export const scoringApi = {
  getSummary: (filter: ScoringAnalyticsFilter = {}) =>
    api.get<ScoringAnalyticsSummary>('/scoring/analytics/summary', filter as Record<string, string>),
  getByTier: async (filter: ScoringAnalyticsFilter = {}): Promise<ScoringBucketAnalytics[]> => {
    const resp = await api.get<TierResponse>('/scoring/analytics/by-tier', filter as Record<string, string>);
    return unwrapBucketArray(resp, 'tiers');
  },
  getByBucket: async (
    filter: ScoringAnalyticsFilter = {}
  ): Promise<{ totalCount: number; buckets: ScoringBucketAnalytics[] }> => {
    const resp = await api.get<BucketResponse>('/scoring/analytics/by-bucket', filter as Record<string, string>);
    if (resp == null) return { totalCount: 0, buckets: [] };
    if (Array.isArray(resp)) return { totalCount: resp.length, buckets: resp };
    return {
      totalCount: typeof resp.totalCount === 'number' ? resp.totalCount : 0,
      buckets: Array.isArray(resp.buckets) ? resp.buckets : [],
    };
  },
  getByFactor: async (filter: ScoringAnalyticsFilter = {}): Promise<ScoringBucketAnalytics[]> => {
    const resp = await api.get<FactorResponse>('/scoring/analytics/by-factor', filter as Record<string, string>);
    return unwrapBucketArray(resp, 'factors');
  },
};

export const signalsApi = {
  getAll: async (
    filters: SignalFilters & { page?: number; pageSize?: number }
  ): Promise<PaginatedResponse<Signal>> => {
    const { eventType, pageSize, ...rest } = filters;
    const params = {
      ...rest,
      ...(eventType != null && eventType !== '' && { event: eventType }),
      limit: pageSize ?? 20,
    } as Record<string, string | number | boolean | undefined>;
    const res = await api.get<SignalsApiResponse>('/signals', params);
    return {
      data: res.data.map(toSignalListItem),
      total: res.pagination.total,
      page: res.pagination.page,
      pageSize: res.pagination.limit,
      totalPages: res.pagination.pages,
    };
  },
  getById: (id: string) => api.get<SignalDetailResponse>(`/signals/${id}`),
  replay: (id: string) =>
    api.post<{ status: 'accepted' | 'duplicate'; signal_id: string; received_at?: string; original_signal_id?: string }>(
      `/signals/${id}/replay`
    ),
};

export interface ActiveCooldownItem {
  id: string;
  symbol: string;
  reason: string;
  expiresAt: string;
  /** Set for per-user loss/stopout; omitted for global (e.g. volatility shock). */
  userId?: string;
}

export interface ActiveCooldownsResponse {
  items: ActiveCooldownItem[];
}

export const riskApi = {
  getStatus: () => api.get<RiskStatus>('/risk/status'),
  getEvents: () => api.get<RiskEvent[]>('/risk/events'),
  getActiveCooldowns: () => api.get<ActiveCooldownsResponse>('/risk/cooldowns'),
  enablePreservation: (justification: string) =>
    api.post<void>('/risk/preservation', { justification }),
  pauseStrategy: (justification: string) =>
    api.post<void>('/risk/pause', { justification }),
  resumeStrategy: (justification: string) =>
    api.post<void>('/risk/resume', { justification }),
  resetDrawdownBaseline: (justification: string) =>
    api.post<void>('/risk/reset-drawdown-baseline', { justification }),
  killAll: (justification: string) =>
    api.post<void>('/risk/kill', { justification }),
};

export const systemApi = {
  syncExchange: () =>
    api.post<{
      success: boolean;
      dbPositionCount: number;
      exchangePositionCount: number;
      discrepancies: unknown[];
      message: string;
    }>('/system/sync-exchange'),
};

export const alertsApi = {
  getAll: (filters: AlertFilters & { page?: number; pageSize?: number }) =>
    api.get<PaginatedResponse<Alert>>('/alerts', filters as Record<string, string | number | boolean | undefined>),
  getRecent: (limit?: number) =>
    api.get<Alert[]>('/alerts/recent', { limit }),
  acknowledge: (id: string, note?: string) =>
    api.post<void>(`/alerts/${id}/acknowledge`, { note }),
  bulkAcknowledge: (ids: string[], note?: string) =>
    api.post<void>('/alerts/bulk-acknowledge', { ids, note }),
};

interface OperatorActionsApiResponse {
  data: Array<{
    id: string;
    createdAt: string;
    operatorId: string;
    actionType: string;
    justification: string;
    relatedEntityType: string | null;
    relatedEntityId: string | null;
  }>;
  pagination: { page: number; limit: number; total: number; pages: number };
}

export const auditApi = {
  getAll: async (
    filters: AuditFilters & { page?: number; pageSize?: number }
  ): Promise<PaginatedResponse<OperatorAction>> => {
    const params: Record<string, string | number | boolean | undefined> = {
      page: filters.page ?? 1,
      limit: filters.pageSize ?? 20,
      ...(filters.operator && { operatorId: filters.operator }),
      ...(filters.actionType && { actionType: filters.actionType }),
      ...(filters.startDate && { from: filters.startDate }),
      ...(filters.endDate && { to: filters.endDate }),
    };
    const res = await api.get<OperatorActionsApiResponse>('/audit', params);
    return {
      data: res.data.map((a) => ({
        id: a.id,
        timestamp: a.createdAt,
        operator: a.operatorId,
        action: a.actionType,
        entity: a.relatedEntityType ?? undefined,
        entityId: a.relatedEntityId ?? undefined,
        justification: a.justification,
      })),
      total: res.pagination.total,
      page: res.pagination.page,
      pageSize: res.pagination.limit,
      totalPages: res.pagination.pages,
    };
  },
};

export interface AutoTradingConfig {
  enabled: boolean;
  allowedSymbols: string[];
  primaryExchangeId?: string | null;
  requireManualConfirmation: boolean;
}

export interface DailyTrendConfigUpdate {
  enabled: boolean;
  minRebalanceFraction: number;
  maxExposure: number;
  catastropheStopPct: number;
}

export interface DailyTrendSettingsResponse {
  config: DailyTrendConfigUpdate & { boundToCurrentUser: boolean };
  status: {
    killSwitchActive: boolean;
    latestState: {
      symbol: string;
      date: string;
      exposure: number;
      realizedVol: number;
      paperRet: number;
      paperEquity: number;
    } | null;
  };
}

export const settingsApi = {
  get: () => api.get<SystemSettings>('/settings'),
  update: (settings: Partial<SystemSettings>, justification: string) =>
    api.put<SystemSettings>('/settings', { ...settings, justification }),
  getTelegramBots: () => api.get<{ bots: TelegramBotConfig[] }>('/settings/telegram-bots'),
  updateTelegramBots: (bots: TelegramBotConfig[]) =>
    api.put<{ bots: TelegramBotConfig[] }>('/settings/telegram-bots', { bots }),
  sendTelegramTestMessage: (bots: TelegramBotConfig[]) =>
    api.post<{ results: { success: boolean; label: string; error?: string }[] }>(
      '/settings/telegram-bots/test',
      { bots }
    ),
  getExchanges: () => api.get<{ exchanges: ExchangeCredential[] }>('/settings/exchanges'),
  addExchange: (data: {
    type: ExchangeType;
    label: string;
    enabled: boolean;
    credentials: Record<string, string>;
  }) =>
    api.post<{ success: boolean; exchange: { id: string; type: ExchangeType; label: string; enabled: boolean } }>(
      '/settings/exchanges',
      data
    ),
  testExchange: (id: string) =>
    api.post<{ success: boolean; error?: string; latencyMs: number }>(
      `/settings/exchanges/${id}/test`,
      {}
    ),
  toggleExchange: (id: string, enabled: boolean) =>
    api.put<{ success: boolean; enabled: boolean }>(
      `/settings/exchanges/${id}/toggle`,
      { enabled }
    ),
  updateExchange: (id: string, data: { label?: string; enabled?: boolean; credentials?: Record<string, string> }) =>
    api.put<{ success: boolean; exchange: { id: string; type: ExchangeType; label: string; enabled: boolean } }>(
      `/settings/exchanges/${id}`,
      data
    ),
  deleteExchange: (id: string) =>
    api.delete<{ success: boolean }>(`/settings/exchanges/${id}`),
  getAutoTrading: () =>
    api.get<{ autoTrading: AutoTradingConfig }>('/settings/auto-trading'),
  updateAutoTrading: (config: AutoTradingConfig) =>
    api.put<{ autoTrading: AutoTradingConfig }>('/settings/auto-trading', config),
  toggleAutoTrading: (enabled: boolean) =>
    api.post<{ enabled: boolean }>('/settings/auto-trading/toggle', { enabled }),
  getTboConfig: () => api.get<TboGlobalConfig>('/settings/tbo-config'),
  updateTboConfig: (config: Partial<TboGlobalConfig>) =>
    api.put<TboGlobalConfig>('/settings/tbo-config', config),
  getScoringSettings: () => api.get<ScoringSettingsValue>('/settings/scoring'),
  updateScoringSettings: (config: Partial<ScoringSettingsValue>) =>
    api.put<ScoringSettingsValue>('/settings/scoring', config),
  getDailyTrend: () =>
    api.get<DailyTrendSettingsResponse>('/settings/daily-trend'),
  updateDailyTrend: (config: DailyTrendConfigUpdate) =>
    api.put<DailyTrendSettingsResponse>('/settings/daily-trend', config),
  getMcpKey: () =>
    api.get<{ mcpEnabled: boolean; hasKey: boolean }>('/settings/mcp-key'),
  regenerateMcpKey: () =>
    api.post<{ key: string; createdAt: string }>('/settings/mcp-key/regenerate'),
  getPositionControls: () => api.get<PositionControls>('/settings/position-controls'),
  updatePositionControls: (
    updates: Partial<PositionControls>,
    justification: string
  ) =>
    api.put<PositionControls>('/settings/position-controls', {
      ...updates,
      justification,
    }),
  exportSettings: async (): Promise<Blob> => {
    const res = await fetch('/api/settings/export', { credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message ?? `Export failed: ${res.status}`);
    }
    return res.blob();
  },
  importSettings: (payload: unknown) =>
    api.post<{
      applied: boolean;
      riskLimits: SystemSettings;
      positionControls: PositionControls;
      autoTrading: AutoTradingConfig | null;
    }>('/settings/import', payload),

  // Settings History
  getSettingsHistory: (params?: {
    page?: number;
    pageSize?: number;
    includePnl?: boolean;
    favoritesOnly?: boolean;
  }) =>
    api.get<{
      items: SettingsHistoryItem[];
      total: number;
      page: number;
      pageSize: number;
    }>('/settings/history', {
      page: params?.page,
      pageSize: params?.pageSize,
      includePnl: params?.includePnl,
      favoritesOnly: params?.favoritesOnly,
    }),
  getSettingsHistoryItem: (id: string, options?: { includePrevious?: boolean }) =>
    api.get<SettingsHistoryDetail>('/settings/history/' + encodeURIComponent(id), {
      includePrevious: options?.includePrevious === true ? true : undefined,
    }),
  /** Same JSON shape as file export; for diff vs snapshots. */
  getExportPayload: () =>
    api.get<unknown>('/settings/export', { inline: true }),
  updateSettingsSnapshot: (id: string, updates: { comment?: string | null; isFavorite?: boolean }) =>
    api.patch<SettingsHistoryDetail>('/settings/history/' + encodeURIComponent(id), updates),
  reapplySettingsSnapshot: (id: string) =>
    api.post<{
      riskLimits: SystemSettings;
      positionControls: PositionControls;
      autoTrading: AutoTradingConfig | null;
    }>('/settings/history/' + encodeURIComponent(id) + '/reapply'),
};

export interface SettingsHistoryItem {
  id: string;
  userId: string;
  name: string | null;
  comment: string | null;
  isFavorite: boolean;
  isReapply: boolean;
  createdAt: string;
  realizedPnl?: number;
}

export interface SettingsHistoryDetail extends SettingsHistoryItem {
  snapshot: unknown;
  createdBy: string | null;
  previousSnapshot?: unknown | null;
  previousSnapshotId?: string | null;
  previousSnapshotCreatedAt?: string | null;
}

export interface Mt5Terminal {
  id: string;
  terminalId: string;
  label: string | null;
  magicNumber: number | null;
  enabled: boolean;
  createdAt: string;
}

export interface Mt5MarketSymbol {
  price: number;
  bid: number;
  ask: number;
  spread: number;
  timestamp: string;
  source?: 'mt5' | 'unavailable';
}

export interface Mt5StatusTerminal {
  id: string;
  terminalId: string;
  label: string | null;
  enabled: boolean;
  lastHeartbeat: {
    timestamp: string;
    terminalConnected: boolean;
    tradeAllowed: boolean;
    accountEquity: number | null;
    accountBalance: number | null;
  } | null;
  pendingCommandCount: number;
  recentExecutionResults: Array<{
    commandId: string;
    success: boolean;
    ticket: number;
    fillPrice: number;
    executedAt: string;
    errorMessage: string | null;
    brokerRetcodeText: string | null;
  }>;
}

export const mt5Api = {
  getStatus: () => api.get<{ terminals: Mt5StatusTerminal[] }>('/mt5/status'),
  getTerminals: () =>
    api.get<{ terminals: Mt5Terminal[] }>('/mt5/terminals'),
  getSymbolMap: () =>
    api.get<{ map: Record<string, string>; mt5Symbols: string[] }>('/mt5/symbol-map'),
  getMarket: () =>
    api.get<{
      symbolMap: Record<string, string>;
      mt5Symbols: string[];
      ticker: Record<string, Mt5MarketSymbol>;
    }>('/mt5/market'),
  syncPositions: () =>
    api.post<{ ok: boolean; created: number; closed: number; skipped: number }>('/mt5/sync-positions'),
  retryCommand: (commandId: string) =>
    api.post<{
      command: {
        id: string;
        terminalId: string;
        symbol: string;
        action: string;
        status: string;
        volume: number | null;
        stopLoss: number | null;
        takeProfit: number | null;
        expiresAt: string;
      };
    }>(`/mt5/commands/${commandId}/retry`),
  addTerminal: (data: {
    terminalId: string;
    label?: string;
    eaSecretToken?: string;
    magicNumber?: number;
  }) =>
    api.post<{
      terminal: Mt5Terminal & { eaSecretToken?: string };
    }>('/mt5/terminals', data),
  updateTerminal: (id: string, data: { label?: string; enabled?: boolean; eaSecretToken?: string; terminalId?: string; magicNumber?: number }) =>
    api.patch<{ terminal: Mt5Terminal }>(`/mt5/terminals/${id}`, data),
  deleteTerminal: (id: string) =>
    api.delete<void>(`/mt5/terminals/${id}`),
};

export const authApi = {
  me: () => api.get<{ email: string; roles: string[] }>('/auth/me'),
  logout: () => api.post<void>('/auth/logout'),
};

export interface UserPreferences {
  uiMode: 'beginner' | 'advanced';
}

export const userPreferencesApi = {
  get: () => api.get<UserPreferences>('/users/me/preferences'),
  update: (uiMode: 'beginner' | 'advanced') =>
    api.put<UserPreferences>('/users/me/preferences', { uiMode }),
};
